import React, { useState, useEffect } from "react";
import {
  MdAdd,
  MdWallpaper,
  MdChevronLeft,
  MdChevronRight,
  MdAutoAwesome,
  MdSync,
  MdOutlinePhotoLibrary,
} from "react-icons/md";
import "./BackgroundListing.css";
import { useBackgroundListing } from "../../customHook/BackgroundListing/useBackgroundListing";
import SearchBar from "../../components/SearchBar/SearchBar";
import BackgroundUploadModal from "../../components/BackgroundUploadModal/BackgroundUploadModal";
import InternalPagination from "../../components/InternalPagination/InternalPagination";
import Toggle from "../../components/Toggle/Toggle";
import ZoomModal from "../../components/ZoomModal/ZoomModal";
import ZoomButton from "../../components/buttons/ZoomButton";
import EditButton from "../../components/buttons/EditButton";
import BackgroundGenerationDrawer from "../../components/BackgroundGenerationDrawer/BackgroundGenerationDrawer";
import AIGenerationsView from "../../components/AIGenerationsView/AIGenerationsView";
import { useBackgroundGenerator } from "../../customHook/BackgroundGeneration/useBackgroundGenerator";

const ExpandableTitle = ({ title = "", className = "" }) => {
  const [showFullTitle, setShowFullTitle] = useState(false);

  return (
    <p
      className={`text-xs font-semibold text-primary cursor-pointer leading-snug transition-all duration-200 min-w-0 flex-1 m-0 hover:text-slate-950 ${
        showFullTitle ? "whitespace-normal break-words" : "truncate"
      } ${className}`}
      onClick={(e) => {
        e.stopPropagation();
        setShowFullTitle((prev) => !prev);
      }}
      title={showFullTitle ? "Click to collapse" : "Click to view full title"}
    >
      {title}
    </p>
  );
};

const BackgroundCardItem = ({
  item,
  isAdmin,
  onToggle,
  onEdit,
  onZoom,
}) => {
  const [currentImgIndex, setCurrentImgIndex] = useState(0);

  const images = Array.isArray(item.images) && item.images.length > 0
    ? item.images.filter(Boolean)
    : [item.front || item.image].filter(Boolean);

  const currentImg = images[currentImgIndex] || images[0] || "";
  const hasMultiple = images.length > 1;
  const isAuto = item.id === "auto" || item.type === "auto";

  const handlePrev = (e) => {
    e.stopPropagation();
    setCurrentImgIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setCurrentImgIndex((prev) => (prev + 1) % images.length);
  };

  return (
    <div
      className="background-card app-card app-card-hover w-full relative text-left overflow-hidden group focus:outline-none flex flex-col cursor-pointer border-outline hover:border-slate-300"
    >
      {/* Edit Button - Visible in Admin mode on top right (Matching Playground) */}
      {isAdmin && onEdit && !isAuto && (
        <EditButton
          onClick={(e) => {
            e.stopPropagation();
            onEdit(item);
          }}
          positionClasses="absolute top-1.5 right-1.5 z-10"
          size="md"
        />
      )}

      {/* Card Image (Matching Playground 4/3 Aspect Ratio) */}
      <div className="w-full aspect-[4/3] bg-surface-container overflow-hidden relative flex items-center justify-center group/img shrink-0">
        {isAuto ? (
          <>
            <div className="absolute inset-0 bg-slate-100 opacity-80" />
            <MdAutoAwesome className="text-[32px] text-secondary/60 relative" />
          </>
        ) : (
          <>
            {/* Tier / Source Badge */}
            {(item.source === "ai" || item.tier === "AI Studio") ? (
              <div className="absolute top-1.5 left-1.5 z-10">
                <span className="inline-flex items-center gap-1 text-[9px] font-medium uppercase tracking-tight px-1.5 py-1 rounded bg-black/60 backdrop-blur-xs text-white border border-white/10 shadow-sm leading-none">
                  <MdAutoAwesome size={10} className="text-amber-300" />
                  <span>AI Studio</span>
                </span>
              </div>
            ) : item.tier ? (
              <div className="absolute top-1.5 left-1.5 z-10">
                <span className="inline-flex items-center text-[9px] font-medium uppercase tracking-tight px-1.5 py-1 rounded bg-black/60 backdrop-blur-xs text-white border border-white/10 shadow-sm leading-none">
                  {item.tier}
                </span>
              </div>
            ) : null}

            {currentImg ? (
              <img
                className="w-full h-full object-cover object-center transition-transform duration-500 hover:scale-105"
                style={{ objectPosition: "center center" }}
                src={currentImg}
                alt={item.name || item.title}
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-slate-300 text-xs">
                No Image
              </div>
            )}

            {/* Left Arrow Button */}
            {hasMultiple && (
              <button
                type="button"
                onClick={handlePrev}
                className="absolute left-1 top-1/2 -translate-y-1/2 z-10 w-5.5 h-5.5 rounded-full bg-white/90 backdrop-blur-sm text-slate-800 flex items-center justify-center hover:bg-white shadow-md transition-all cursor-pointer"
                aria-label="Previous image"
              >
                <MdChevronLeft size={16} />
              </button>
            )}

            {/* Right Arrow Button */}
            {hasMultiple && (
              <button
                type="button"
                onClick={handleNext}
                className="absolute right-1 top-1/2 -translate-y-1/2 z-10 w-5.5 h-5.5 rounded-full bg-white/90 backdrop-blur-sm text-slate-800 flex items-center justify-center hover:bg-white shadow-md transition-all cursor-pointer"
                aria-label="Next image"
              >
                <MdChevronRight size={16} />
              </button>
            )}

            {/* Dot Indicators */}
            {hasMultiple && (
              <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex gap-1 z-10">
                {images.map((_, idx) => (
                  <div
                    key={idx}
                    className={`w-1.5 h-1.5 rounded-full transition-all duration-200 ${
                      idx === currentImgIndex ? "bg-primary" : "bg-white/70"
                    }`}
                  />
                ))}
              </div>
            )}

            {/* Zoom Button */}
            <ZoomButton
              modalTarget="#bgListingZoomModal"
              onClick={(e) => {
                if (e) e.stopPropagation();
                const secondImg =
                  images[(currentImgIndex + 1) % images.length] ||
                  item.back ||
                  "";
                if (onZoom) {
                  onZoom(currentImg, secondImg);
                }
              }}
              positionClasses="absolute bottom-1.5 right-1.5"
              size="md"
            />
          </>
        )}
      </div>

      {/* Card Footer Info (Matching Playground) */}
      <div className="p-2 sm:p-3 border-t border-slate-200/70 bg-white w-full min-w-0 flex items-center justify-between gap-1.5 sm:gap-2.5">
        <ExpandableTitle title={item.name || item.title} className="text-xs sm:text-[13px]" />
        {isAdmin && !isAuto && (
          <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
            <Toggle
              checked={
                item.bIsActive === 1 ||
                item.bIsActive === true ||
                (item.bIsActive === undefined &&
                  (item.isActive === 1 ||
                    item.isActive === true ||
                    item.isActive === undefined))
              }
              onChange={() => onToggle && onToggle(item)}
              size="sm"
            />
          </div>
        )}
      </div>
    </div>
  );
};

const BackgroundListing = () => {
  const {
    userRole,
    gender,
    onGenderChange,
    source,
    onSourceChange,
    searchTerm,
    onSearchChange,
    backgrounds,
    loading,
    error,
    refreshBackgrounds,
    pagination,
    onPageChange,
    onLimitChange,
    handleToggleBackground,
    // Modals
    isModalOpen,
    backgroundToEdit,
    isUploadLoading,
    handleOpenUploadModal,
    handleOpenEditModal,
    handleCloseModal,
    handleUploadSubmit,
    // Zoom
    zoomModal,
    openZoomModal,
    closeZoomModal,
    addBackgroundLocally,
  } = useBackgroundListing();

  const {
    isDrawerOpen: isGenDrawerOpen,
    openDrawer: openGenDrawer,
    closeDrawer: closeGenDrawer,
    jobs: genJobs,
    history: genHistory,
    historyStats: genStats,
    pagination: genPagination,
    filters: genFilters,
    isFetchingHistory: isGenFetching,
    fetchHistory: fetchGenHistory,
    startGeneration,
    retryJob,
    cancelJob,
    dismissJob,
    saveToLibrary,
    deleteHistoryItem,
  } = useBackgroundGenerator({
    onAddBackgroundLocally: addBackgroundLocally,
  });

  const [activeMainTab, setActiveMainTab] = useState("library"); // "library" | "ai-studio"
  const generatingCount = genJobs.filter((j) => j.status === "generating").length;

  React.useEffect(() => {
    if (activeMainTab === "ai-studio") {
      fetchGenHistory({ page: 1, limit: genPagination?.limit || 10 });
    }
  }, [activeMainTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const isAdmin = userRole === "admin" || userRole === "superadmin";

  const pageHeaderElement = (
    <div className="product-selector-header px-4 sm:px-6 pt-3 sm:pt-4 pb-2 sm:pb-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5 sm:gap-4">
      <div>
        <div className="flex items-center gap-2.5">
          <h1 className="font-headline-md text-lg sm:text-[20px] text-primary font-bold tracking-tight m-0">
            Backgrounds
          </h1>
          <span className="font-body-md inline-flex items-center gap-1.5 text-primary bg-primary/[0.04] border border-primary/10 px-2.5 py-0.5 rounded-full text-[10px] sm:text-xs font-bold shadow-[0_1px_2px_rgba(0,0,0,0.02)] whitespace-nowrap">
            <span className="w-1.5 h-1.5 rounded-full bg-primary/40" />
            {pagination?.total || 0} {(pagination?.total || 0) === 1 ? "Background" : "Backgrounds"}
          </span>
        </div>
        <p className="font-body-md text-[10px] sm:text-xs text-secondary mt-0.5">
          Manage studio backdrops, lifestyle environments, and virtual scenery.
        </p>
      </div>

      {/* Header Right Actions: Segmented Tab Switcher + Action Button */}
      <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-3 w-full sm:w-auto">
        {/* Segmented Tab Switcher: Dark Active Design with Standard Corners */}
        <div className="flex items-center bg-slate-100/90 border border-slate-200/90 p-0.5 rounded gap-0.5 shadow-2xs">
          {/* Library Tab */}
          <button
            type="button"
            onClick={() => {
              if (activeMainTab === "library") {
                refreshBackgrounds();
              } else {
                setActiveMainTab("library");
              }
            }}
            className={`flex items-center gap-1 sm:gap-2 px-2.5 sm:px-3 py-1.5 rounded text-xs transition-all duration-150 cursor-pointer ${
              activeMainTab === "library"
                ? "bg-primary text-white font-bold shadow-xs border-none"
                : "bg-white/70 hover:bg-white text-slate-600 hover:text-primary border border-slate-200/80 hover:border-slate-300 font-semibold shadow-2xs"
            }`}
          >
            <MdOutlinePhotoLibrary
              size={15}
              className={activeMainTab === "library" ? "text-white" : "text-slate-500"}
            />
            <span>Library</span>
            <span
              className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold transition-colors ${
                activeMainTab === "library"
                  ? "bg-white/20 text-white border border-white/20"
                  : "bg-slate-200 text-slate-800 border border-slate-200/80"
              }`}
            >
              {pagination?.total || 0}
            </span>
          </button>

          {/* AI Studio Tab */}
          <button
            type="button"
            onClick={() => {
              if (activeMainTab === "ai-studio") {
                fetchGenHistory({ page: 1, limit: genPagination?.limit || 10 });
              } else {
                setActiveMainTab("ai-studio");
              }
            }}
            className={`flex items-center gap-1 sm:gap-2 px-2.5 sm:px-3 py-1.5 rounded text-xs transition-all duration-150 cursor-pointer ${
              activeMainTab === "ai-studio"
                ? "bg-primary text-white font-bold shadow-xs border-none"
                : "bg-white/70 hover:bg-white text-slate-600 hover:text-primary border border-slate-200/80 hover:border-slate-300 font-semibold shadow-2xs"
            }`}
          >
            <MdAutoAwesome
              size={14}
              className={
                generatingCount > 0
                  ? "text-amber-300 animate-spin"
                  : activeMainTab === "ai-studio"
                  ? "text-white"
                  : "text-slate-500"
              }
            />
            <span>AI Studio</span>
            {generatingCount === 0 && (
              <span
                className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold transition-colors ${
                  activeMainTab === "ai-studio"
                    ? "bg-white/20 text-white border border-white/20"
                    : "bg-slate-200 text-slate-800 border border-slate-200/80"
                }`}
              >
                {genStats?.total ?? genPagination?.total ?? genHistory.length}
              </span>
            )}
          </button>
        </div>

        {/* Context-aware Action Button: Styled to match Actions */}
        {isAdmin && (
          activeMainTab === "library" ? (
            <button
              type="button"
              onClick={handleOpenUploadModal}
              className="app-button-secondary flex items-center justify-center gap-1 sm:gap-1.5 px-2.5 sm:px-3.5 h-8 rounded-md text-xs font-semibold bg-white border border-slate-300 hover:border-slate-400 text-slate-700 shadow-sm focus:outline-none cursor-pointer whitespace-nowrap shrink-0 transition-all"
              title="Add Background"
            >
              <MdAdd size={16} />
              <span className="hidden sm:inline">Add Background</span>
              <span className="sm:hidden">Add</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={openGenDrawer}
              className="app-button-secondary flex items-center justify-center gap-1 sm:gap-1.5 px-2.5 sm:px-3.5 h-8 rounded-md text-xs font-semibold bg-white border border-slate-300 hover:border-slate-400 text-slate-700 shadow-sm focus:outline-none cursor-pointer whitespace-nowrap shrink-0 transition-all"
              title="Generate Background"
            >
              <MdAutoAwesome size={15} />
              <span className="hidden sm:inline">Generate Background</span>
              <span className="sm:hidden">Generate</span>
            </button>
          )
        )}
      </div>
    </div>
  );

  return (
    <div className="app-page background-listing-screen flex-1 flex flex-col h-full min-h-0 overflow-hidden">
      {/* TAB 1: LIBRARY CATALOG */}
      {activeMainTab === "library" && (
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {/* Scrollable Content Body */}
          <div className="flex-1 overflow-y-auto min-h-0">
            {/* Page Header (Scrolls away, not sticky) */}
            {pageHeaderElement}

            {/* Primary Controls Bar (Sticky at top when scrolling) */}
            <div className="product-controls-wrapper sticky top-0 z-30 px-4 sm:px-6 py-2 bg-[#F1F4F9]/95 backdrop-blur-sm">
              <div className="app-control-bar product-controls-shell flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 sm:gap-4 p-2.5 sm:p-3">
                {/* Gender selector tabs */}
                <div className="flex flex-wrap items-center gap-2 max-w-full shrink-0">
                  <div className="gender-tabs flex gap-0.5 bg-slate-100/80 p-0.5 rounded-md shrink-0">
                    <button
                      onClick={() => onGenderChange("women")}
                      className={`px-3 sm:px-4 py-1 rounded font-semibold text-xs transition-all duration-200 ${
                        gender === "women"
                          ? "bg-primary text-on-primary shadow-sm font-bold border-none"
                          : "bg-white text-slate-500 border border-slate-200 hover:text-primary hover:border-slate-300 hover:shadow-sm"
                      }`}
                      type="button"
                    >
                      Women
                    </button>
                    <button
                      onClick={() => onGenderChange("men")}
                      className={`px-3 sm:px-4 py-1 rounded font-semibold text-xs transition-all duration-200 ${
                        gender === "men"
                          ? "bg-primary text-on-primary shadow-sm font-bold border-none"
                          : "bg-white text-slate-500 border border-slate-200 hover:text-primary hover:border-slate-300 hover:shadow-sm"
                      }`}
                      type="button"
                    >
                      Men
                    </button>
                  </div>

                  {/* Source Selector: All, Upload, AI */}
                  <div className="gender-tabs flex gap-0.5 bg-slate-100/80 p-0.5 rounded-md shrink-0">
                    <button
                      type="button"
                      onClick={() => onSourceChange("all")}
                      className={`px-2.5 sm:px-3 py-1 rounded font-semibold text-xs transition-all duration-200 ${
                        source === "all"
                          ? "bg-primary text-on-primary shadow-sm font-bold border-none"
                          : "bg-white text-slate-500 border border-slate-200 hover:text-primary hover:border-slate-300 hover:shadow-sm"
                      } focus:outline-none`}
                    >
                      All Sources
                    </button>
                    <button
                      type="button"
                      onClick={() => onSourceChange("upload")}
                      className={`px-2.5 sm:px-3 py-1 rounded font-semibold text-xs transition-all duration-200 ${
                        source === "upload"
                          ? "bg-primary text-on-primary shadow-sm font-bold border-none"
                          : "bg-white text-slate-500 border border-slate-200 hover:text-primary hover:border-slate-300 hover:shadow-sm"
                      } focus:outline-none`}
                    >
                      Uploads
                    </button>
                    <button
                      type="button"
                      onClick={() => onSourceChange("ai")}
                      className={`px-2.5 sm:px-3 py-1 rounded font-semibold text-xs transition-all duration-200 flex items-center gap-1 ${
                        source === "ai"
                          ? "bg-primary text-on-primary shadow-sm font-bold border-none"
                          : "bg-white text-slate-500 border border-slate-200 hover:text-primary hover:border-slate-300 hover:shadow-sm"
                      } focus:outline-none`}
                    >
                      <MdAutoAwesome size={12} className={source === "ai" ? "text-amber-300" : "text-amber-500"} />
                      <span>AI Studio</span>
                    </button>
                  </div>
                </div>

                {/* Search Input & Mobile Action Button */}
                <div className="product-search-actions flex items-center gap-1.5 sm:gap-2 sm:justify-end relative min-w-0 w-full sm:w-auto">
                  <SearchBar
                    value={searchTerm}
                    onChange={onSearchChange}
                    placeholder="Search backgrounds..."
                    className="flex-1 min-w-0 w-full sm:w-64"
                  />
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={handleOpenUploadModal}
                      className="sm:hidden app-button-primary flex items-center justify-center gap-1 px-2.5 h-8 rounded-md text-xs font-semibold shadow-sm focus:outline-none cursor-pointer whitespace-nowrap shrink-0"
                      title="Add Background"
                    >
                      <MdAdd size={16} />
                      <span>Add</span>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Content Body Grid */}
            <div className="px-4 sm:px-6 pt-2 pb-6">
              {loading ? (
                <div className="flex-1 flex flex-col items-center justify-center py-20 w-full text-center">
                  <MdSync className="text-[36px] text-slate-400 animate-spin mb-2" />
                  <p className="text-sm font-semibold text-secondary">Loading studio backgrounds...</p>
                </div>
              ) : error ? (
                <div className="flex flex-col items-center justify-center py-20 text-center bg-red-50 border border-red-200 rounded-xl p-6">
                  <p className="text-sm font-semibold text-red-600 mb-2">{error}</p>
                  <button
                    type="button"
                    onClick={refreshBackgrounds}
                    className="px-3 py-1.5 bg-white border border-red-300 rounded-lg text-xs font-semibold text-red-700 shadow-sm hover:bg-red-50"
                  >
                    Retry
                  </button>
                </div>
              ) : backgrounds.length === 0 ? (
                <div className="app-empty-state flex flex-col items-center justify-center py-20 w-full gap-2 bg-white rounded-2xl border border-slate-200/80 p-8 shadow-2xs">
                  <MdWallpaper className="text-[48px] text-slate-300 mb-2" />
                  <p className="text-sm font-semibold text-slate-700">No backgrounds found matching your filters</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-5 gap-x-3 sm:gap-x-6 gap-y-4 sm:gap-y-7 lg:gap-y-8 items-start">
                  {backgrounds.map((bg) => (
                    <BackgroundCardItem
                      key={bg.id}
                      item={bg}
                      isAdmin={isAdmin}
                      onToggle={handleToggleBackground}
                      onEdit={handleOpenEditModal}
                      onZoom={openZoomModal}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Always Visible Footer Bar for Library */}
          <footer className="app-footer-bar shrink-0 border-t border-slate-200/90 bg-white/95 backdrop-blur-sm px-3 sm:px-6 py-1.5 z-20 shadow-[0_-2px_8px_rgba(26,32,48,0.03)] flex items-center min-h-[38px] overflow-x-auto">
            <InternalPagination
              page={pagination?.page || 1}
              limit={pagination?.limit || 10}
              total={pagination?.total || 0}
              totalPages={pagination?.totalPages || 1}
              limitOptions={[10, 30, 50]}
              onPageChange={onPageChange}
              onLimitChange={onLimitChange}
              showFirstLast={true}
              rowsLabel="Items per page:"
            />
          </footer>
        </div>
      )}

      {/* TAB 2: AI GENERATIONS & QUEUE */}
      {activeMainTab === "ai-studio" && (
        <AIGenerationsView
          header={pageHeaderElement}
          jobs={genJobs}
          history={genHistory}
          historyStats={genStats}
          pagination={genPagination}
          filters={genFilters}
          isFetching={isGenFetching}
          onFetchHistory={fetchGenHistory}
          onRetry={retryJob}
          onCancel={cancelJob}
          onDismiss={dismissJob}
          onSaveToLibrary={saveToLibrary}
          onZoom={(img) => openZoomModal(img, "")}
          onOpenDrawer={openGenDrawer}
          onDeleteHistoryItem={deleteHistoryItem}
          modalTarget="#bgListingZoomModal"
        />
      )}

      {/* Upload/Edit Modal */}
      <BackgroundUploadModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleUploadSubmit}
        isLoading={isUploadLoading}
        backgroundToEdit={backgroundToEdit}
      />

      {/* Zoom Modal */}
      <ZoomModal
        modalId="bgListingZoomModal"
        front={zoomModal.front}
        back={zoomModal.back}
        onClose={closeZoomModal}
        isBackground={true}
      />

      {/* AI Background Generation Drawer */}
      <BackgroundGenerationDrawer
        isOpen={isGenDrawerOpen}
        onClose={closeGenDrawer}
        onGenerate={async (params) => {
          const res = await startGeneration(params);
          if (res?.success) {
            closeGenDrawer();
            setActiveMainTab("ai-studio"); // Seamlessly switch to AI tab so user watches progress!
          }
          return res;
        }}
        defaultGender={gender}
        activeJobsCount={generatingCount}
      />
    </div>
  );
};

export default BackgroundListing;
